import { Entity, Insight, Isochrone } from '.'

export enum Action {
  Share = 'share',
  Assets = 'load_assets',
  AssetsPagination = 'load_assets_pagination',
  Plan = 'load_plan',
  Geoboundary = 'geoboundary_geometries',
  CustomPOIs = 'custom_POIs',
  Asset = 'asset_details',
  Insights = 'insights',
  GetSharedPlanInfo = 'get_shared_plan_info',
  SharedExportPoisAssets = 'shared_export_pois_assets',
  SharedExportAllPois = 'shared_export_all_pois',
  SharedExportAudiencesAssets = 'shared_export_audiences_assets',
  SharedExportAssetsPOIsDistance = 'shared_export_assets_pois_distance',
  ExistCustomPOIsGroups = 'exist_custom_pois_groups',
  Isochrones = 'isochrones',
  AudiencesQuintiles = 'audiences_quintiles',
  AdvanceOptions = 'advance_options',
  AllMediaTypeOptions = 'all_media_type_options',
  /** POI icon assignments, served only when the legend has icons enabled. */
  PoisIcons = 'pois_icons',
  MappingPOIsBrands = 'mapping_pois_brands',
  MappingPOIsTags = 'mapping_pois_tags',
  MappingPOIsTopCategories = 'mapping_pois_top_categories',
  MappingPOIsSubCategories = 'mapping_pois_sub_categories',
}

export enum SharePlanErrorCode {
  PlanNotFound = 'PlanNotFoundError',
  SharedPlanInvalidPassword = 'SharedPlanInvalidPasswordError',
  SharedPlanNotEnabled = 'SharedPlanNotEnabledError',
  SharedPlanExpired = 'SharedPlanExpiredError',
}

export interface SharePlanProperties {
  id: string
  password?: string
}

export interface CreateSharedPlanParameters {
  planId: string
  appVersion: string
  password?: string
  assetsOffset?: number
  token?: string
  iv?: string
  customPoisIds?: string[]
  isochronesFilter?: Isochrone.IsochronesFilter
  expiracyDate?: string
  mappingValues?: string[]
}

export interface AssetProperties {
  address: string
  external_panel_id: string
  geom: never
  id: string
  internal_panel_id: string
  impressions: number
  latitude: number
  longitude: number
  name: string
  l1_panel_class: string
  l1panelclass_id: number
  l2product_id: number
  l2_product: string
  state_id: number
  dmas_id: number
  congresional_district_id: number
  cbsa_id: number
  county_id: number
  city_id: string
  zip_code_id: string
  zip_code_name: string
  ad_region_text: string
  title: string
  excluded?: boolean
}

export interface GeoJsonAsset {
  type: 'Feature'
  geometry: {
    coordinates: [number, number]
    type: 'Point'
  }
  properties: AssetProperties
}

export interface RemoteAssetResolution {
  count: number
  assets: GeoJsonAsset[]
  token?: string
  iv?: string
}

export interface SharedPlanInsights {
  demographics: Insight.InsightResult[]
  topindex: Insight.InsightResult[]
  overview: Insight.InsightResult[]
  impressions: Insight.InsightImpressionsResult[]
}

export interface AdvanceOptionsExportColumnSelection {
  id: string
  name: string
  selected: boolean
}

export interface AdvanceOptions {
  exportSelection: {
    enabled: boolean
    frames: boolean
    pois: boolean
    proximity: boolean
    frameColumns: AdvanceOptionsExportColumnSelection[] | null
    poisColumns: AdvanceOptionsExportColumnSelection[] | null
    proximityColumns: AdvanceOptionsExportColumnSelection[] | null
  }
  mapViewOnly: boolean
  showExcludedFrames: boolean
  layerSelection: {
    enabled: boolean
    frames: boolean
    pois: boolean
    heatmap: boolean
    geoboundaries: boolean
  }
}

export interface SharedPlan extends Entity.Entity, Entity.Auditable {
  planId: string
  passwordRequire: boolean
  originalPassword?: string
  url: string
  expiracyDate: Date
  createdByUserEmail: string
  creationDate: Date
  password?: string
  enabled: boolean
  advanceOptions?: AdvanceOptions
}

export interface CreateSharePlanBodyParams {
  password?: string
  showPrice?: 'true' | 'false'
  isochronesFilter?: Isochrone.IsochronesFilter
  expiracyDate?: string
}

export interface IsPasswordRequired {
  passwordRequired: boolean
}

export interface GetSharedPlanInfoResponse {
  code?: SharePlanErrorCode
  isActive?: boolean
  passwordRequired: boolean
  isPlanNotFound?: boolean
  isExpired?: boolean
}

export interface SharedPlanState {
  isActive: boolean
  isPasswordRequired: boolean
  isPlanNotFound: boolean
  isExpired: boolean
}

export interface SharePlanAdvanceOptionsVisibility {
  advance_options: boolean
  map: {
    enabled: boolean
    map_view_only: boolean
    show_excluded_frames: boolean
    layers_selection: boolean
  }
  export: boolean
}

export const DEFAULT_SHARED_PLAN_ADVANCE_OPTIONS: AdvanceOptions = {
  mapViewOnly: false,
  showExcludedFrames: false,
  layerSelection: {
    enabled: true,
    frames: true,
    pois: true,
    heatmap: true,
    geoboundaries: true,
  },
  exportSelection: {
    enabled: true,
    frames: true,
    pois: true,
    proximity: true,
    frameColumns: null,
    poisColumns: null,
    proximityColumns: null,
  },
}
